import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { riskService } from '../../../services/riskService';
import { useProtocols } from '../../../hooks/useProtocols';
import { useNotifications } from '../../NotificationSystem';
import './SetupWizardSteps.css';

const Step8Risks = ({ stepData, onComplete, onValidation, userData, structure }) => {
  const { notify } = useNotifications();
  const { t } = useTranslation('setup');
  const [loading, setLoading] = useState(false);
  const [risks, setRisks] = useState([]);
  const organizationId = structure?.organizations?.[0]?.id || null;
  const { protocols } = useProtocols({});

  useEffect(() => {
    const loadRisks = async () => {
      try {
        const allRisks = await riskService.getAllRisks();
        setRisks(allRisks || []);
        if ((allRisks || []).length >= 3) {
          onComplete({ risks: allRisks.length, requirementsMet: true });
        }
      } catch (error) {
        console.error('Error loading risks:', error);
      }
    };
    loadRisks();
  }, []);

  const handleCreateBasicRisks = async () => {
    if (!organizationId) {
      notify.error(t('step8.notifications.missingOrganization'), t('step8.notifications.missingOrganizationTitle'));
      return;
    }

    if (!protocols || protocols.length === 0) {
      notify.error(t('step8.notifications.missingProtocols'), t('step8.notifications.missingProtocolsTitle'));
      return;
    }

    setLoading(true);
    try {
      const userId = userData?.uid || 'system';
      const protocolId = protocols[0].id;
      const it = 'step8.defaultRisks.infectionTransmission';
      const ec = 'step8.defaultRisks.environmentalContamination';
      const ppe = 'step8.defaultRisks.ppeFailure';

      const basicRisks = [
        {
          protocol_id: protocolId,
          risk_name: t(`${it}.name`),
          risk_description: t(`${it}.description`),
          severity: 8,
          occurrence: 6,
          detection: 5,
          risk_type: 'clinical',
          category: 'infection_control',
          mitigation_strategies: [t(`${it}.mitigation1`), t(`${it}.mitigation2`)],
          is_active: true
        },
        {
          protocol_id: protocolId,
          risk_name: t(`${ec}.name`),
          risk_description: t(`${ec}.description`),
          severity: 7,
          occurrence: 5,
          detection: 4,
          risk_type: 'environmental',
          category: 'infection_control',
          mitigation_strategies: [t(`${ec}.mitigation1`), t(`${ec}.mitigation2`)],
          is_active: true
        },
        {
          protocol_id: protocolId,
          risk_name: t(`${ppe}.name`),
          risk_description: t(`${ppe}.description`),
          severity: 9,
          occurrence: 4,
          detection: 6,
          risk_type: 'safety',
          category: 'infection_control',
          mitigation_strategies: [t(`${ppe}.mitigation1`), t(`${ppe}.mitigation2`)],
          is_active: true
        }
      ];

      for (const risk of basicRisks) {
        await riskService.createRisk(risk, userId);
      }

      const allRisks = await riskService.getAllRisks();
      setRisks(allRisks || []);
      
      notify.success(t('step8.notifications.createdSuccess'), t('step8.notifications.createdSuccessTitle'));
      onComplete({ risks: (allRisks || []).length, requirementsMet: true });
    } catch (error) {
      console.error('Error creating risks:', error);
      notify.error(t('step8.notifications.createFailed'), t('step8.notifications.createFailedTitle'));
    } finally {
      setLoading(false);
    }
  };

  const requirements = {
    risks: risks.length >= 3
  };

  return (
    <div className="step-content">
      <div className="step-section">
        <h3>{t('step8.title')}</h3>
        <p>{t('step8.description')}</p>
      </div>

      {!organizationId && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
          {t('step8.completeStep1Warning')}
        </div>
      )}

      {(!protocols || protocols.length === 0) && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
          {t('step8.completeStep7Warning')}
        </div>
      )}

      <div className="quick-setup-section">
        <div className="quick-setup-option" style={{ cursor: loading || !organizationId || !protocols || protocols.length === 0 ? 'not-allowed' : 'pointer', opacity: loading || !organizationId || !protocols || protocols.length === 0 ? 0.6 : 1 }}>
          <div style={{ textAlign: 'center' }}>
            <h3>{t('step8.quickSetupTitle')}</h3>
            <p>{t('step8.quickSetupDescription')}</p>
            <ul style={{ textAlign: 'left', marginTop: '12px', paddingLeft: '20px' }}>
              <li>{t('step8.riskList.infectionTransmission')}</li>
              <li>{t('step8.riskList.environmentalContamination')}</li>
              <li>{t('step8.riskList.ppeFailure')}</li>
            </ul>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCreateBasicRisks}
              disabled={loading || !organizationId || !protocols || protocols.length === 0}
              style={{ marginTop: '16px' }}
            >
              {loading ? t('step8.creating') : t('step8.createBasicRisks')}
            </button>
          </div>
        </div>
      </div>

      <div className="requirements-section" style={{ marginTop: '32px' }}>
        <h3>{t('step8.risksTitle')}</h3>
        <div className="requirements-list">
          <div className={`requirement-item ${requirements.risks ? 'met' : 'pending'}`}>
            {requirements.risks ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step8.risksCount', { count: risks.length })}</span>
          </div>
        </div>

        {requirements.risks && (
          <div className="success-message" style={{ marginTop: '20px' }}>
            {t('step8.minimumMet')}
          </div>
        )}
      </div>

      <div className="manual-setup-section" style={{ marginTop: '32px' }}>
        <p style={{ color: '#666', fontStyle: 'italic' }}>
          {t('step8.manualSetupNote')}
        </p>
      </div>
    </div>
  );
};

export default Step8Risks;
